import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {ReplaceWarningInfoKey} from 'molbio/biointelligent-links/replace-link/constants';
import PaneWithSidebar from 'molbio/biointelligent-links/replace-link/PaneWithSidebar';
import {
  FONT_PROXIMA_14,
  FONT_PROXIMA_13,
  FONT_PROXIMA_11,
  FONT_WEIGHT_PROXIMA_SEMIBOLD,
} from 'styles/FontConstants';

const WARNING_KEY_TO_METADATA: {
  [warningKey in ReplaceWarningInfoKey]: {color: string; explanationCopy: string};
} = {
  [ReplaceWarningInfoKey.OVERLAPPING_LINKS]: {
    color: Color.RED_400,
    explanationCopy: 'Loss of overlapping part, translation link, or transcription link',
  },
  [ReplaceWarningInfoKey.REMOVED_FEATURES]: {
    color: Color.ORANGE_400,
    explanationCopy: 'Loss of overlapping annotations',
  },
  [ReplaceWarningInfoKey.MUTATED_FEATURES]: {
    color: Color.PURPLE_400,
    explanationCopy: 'Naturally resolved change to annotation or translation',
  },
};

const ORDERED_WARNING_KEYS = [
  ReplaceWarningInfoKey.OVERLAPPING_LINKS,
  ReplaceWarningInfoKey.REMOVED_FEATURES,
  ReplaceWarningInfoKey.MUTATED_FEATURES,
];

const INFO_ICON_SIZE = 8;

interface InfoIconProps {
  warningKey: ReplaceWarningInfoKey;
}

function InfoIcon(props: InfoIconProps): JSX.Element {
  return (
    <svg width={INFO_ICON_SIZE} height={INFO_ICON_SIZE} className={css(styles.legendIcon)}>
      <circle
        cx={INFO_ICON_SIZE / 2}
        cy={INFO_ICON_SIZE / 2}
        r={INFO_ICON_SIZE / 2}
        fill={WARNING_KEY_TO_METADATA[props.warningKey].color}
      />
    </svg>
  );
}

export interface ConfirmPropagationPaneProps {
  sidebarExpanded: boolean;
  onToggleSidebarExpanded: () => void;
}

export default function ConfirmPropagationPane(props: ConfirmPropagationPaneProps): JSX.Element {
  return (
    <PaneWithSidebar
      sidebarExpanded={props.sidebarExpanded}
      onToggleSidebarExpanded={props.onToggleSidebarExpanded}
      sidebarContent={
        <>
          <p className={css(styles.helpText)}>
            Confirm selected sequences you want to update by replacing the current link with the updated link
          </p>
          <p className={css(styles.helpText)}>
            Replacing the link may result in a loss of overlapping links and/or features. See legend to review
            issues on selected sequences
          </p>

          <div className={css(styles.legendLabel)}>Issue legend</div>
          <div className={css(styles.legend)}>
            {ORDERED_WARNING_KEYS.map((warningKey: ReplaceWarningInfoKey, i: number) => (
              <div
                key={warningKey}
                className={css(
                  styles.legendItem,
                  i !== ORDERED_WARNING_KEYS.length && styles.notLastLegendItem
                )}
              >
                <InfoIcon warningKey={warningKey} />
                <span>{WARNING_KEY_TO_METADATA[warningKey].explanationCopy}</span>
              </div>
            ))}
          </div>
        </>
      }
    >
      <div>Content goes here</div>
    </PaneWithSidebar>
  );
}

const styles = StyleSheet.create({
  helpText: {
    ...FONT_PROXIMA_14,
    marginBottom: '24px',
  },
  legendLabel: {
    ...FONT_PROXIMA_11,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    textTransform: 'uppercase',
    color: Color.GRAY_600,
    marginBottom: '8px',
  },
  legend: {
    ...FONT_PROXIMA_13,
    borderRadius: '8px',
    padding: '8px 12px',
    border: `1px solid ${Color.GRAY_200}`,
  },
  legendItem: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '8px',
  },
  notLastLegendItem: {
    marginBottom: '4px',
  },
  legendIcon: {
    flexShrink: 0,
  },
});
